"use client";

import { captureClientEvent } from "@/lib/telemetry-client";
import { getStripe, resetStripe } from "@/utils/stripe-client";
import { Elements } from "@stripe/react-stripe-js";
import { Stripe, StripeElementsOptions } from "@stripe/stripe-js";
import { useEffect, useMemo, useState } from "react";
import { FaSpinner, FaLock, FaArrowsRotate } from "react-icons/fa6";
import CheckoutStripe from "./checkout-stripe";

interface CheckoutElementProps {
    clientSecret: string;
    paymentMethod: string | undefined;
    email: string;
    total: number;
    isLoading: boolean;
    paymentError: boolean;
    caravan: string;
    kfz: string;
    selectedTicket: string;
    returnPath?: string;
}

export default function CheckoutElement({ 
    clientSecret, 
    paymentMethod, 
    email, 
    total, 
    isLoading, 
    paymentError, 
    caravan, 
    kfz, 
    selectedTicket,
    returnPath = "/tickets/success",
}: CheckoutElementProps) {
    const [stripe, setStripe] = useState<Stripe | null>(null);
    const [stripeError, setStripeError] = useState<string | null>(null);
    const [stripeLoadAttempt, setStripeLoadAttempt] = useState(0);
    const [autoRetryTriggered, setAutoRetryTriggered] = useState(false);
    const hasRequiredFields = useMemo(
        () => Boolean(clientSecret && paymentMethod && !isLoading && email && total && kfz && (selectedTicket === "caravan" ? caravan : true)),
        [caravan, clientSecret, email, isLoading, kfz, paymentMethod, selectedTicket, total]
    );

    useEffect(() => {
        if (!hasRequiredFields) {
            setStripe(null);
            setStripeError(null);
            setAutoRetryTriggered(false);
            return;
        }

        let isCancelled = false;
        setStripe(null);
        setStripeError(null);

        (async () => {
            try {
                const stripeInstance = await getStripe();
                if (isCancelled) return;
                if (!stripeInstance) {
                    throw new Error("Stripe configuration missing");
                }
                setStripe(stripeInstance);
                captureClientEvent("checkout_stripe_js_loaded", {
                    payment_method: paymentMethod,
                    ticket_type: selectedTicket,
                });
            } catch (error) {
                if (isCancelled) return;
                const message = error instanceof Error ? error.message : "unknown";
                resetStripe();
                if (!autoRetryTriggered) {
                    setAutoRetryTriggered(true);
                    setStripeLoadAttempt((current) => current + 1);
                    captureClientEvent("checkout_stripe_js_load_retry_scheduled", {
                        payment_method: paymentMethod,
                        ticket_type: selectedTicket,
                        error: message,
                    });
                    return;
                }
                setStripeError("Das Zahlungsformular konnte nicht geladen werden. Bitte Seite neu laden oder Zahlungsmethode wechseln.");
                captureClientEvent("checkout_stripe_js_load_failed", {
                    payment_method: paymentMethod,
                    ticket_type: selectedTicket,
                    error: message,
                    attempt: stripeLoadAttempt + 1,
                });
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [autoRetryTriggered, hasRequiredFields, paymentMethod, selectedTicket, stripeLoadAttempt]);

    const options: StripeElementsOptions = {
        clientSecret,
        appearance: {
            theme: 'stripe',
            labels: 'floating',
            variables: {
                colorPrimary: '#1e293b',
                colorBackground: '#ffffff',
                colorText: '#1e293b',
                colorDanger: '#dc2626',
                fontFamily: 'system-ui, -apple-system, sans-serif',
                spacingUnit: '4px',
                borderRadius: '12px',
                fontSizeBase: '16px',
            },
            rules: {
                '.Input': {
                    border: '2px solid #e2e8f0',
                    boxShadow: 'none',
                    padding: '12px 16px',
                },
                '.Input:focus': {
                    border: '2px solid #fbbf24',
                    boxShadow: '0 0 0 3px rgba(251, 191, 36, 0.1)',
                },
                '.Label': {
                    fontWeight: '500',
                },
            },
        },
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center rounded-xl bg-slate-50 p-8">
                <FaSpinner className="h-8 w-8 animate-spin text-amber-500" />
                <p className="mt-3 text-sm text-slate-500">Zahlungsformular wird geladen...</p>
            </div>
        );
    }

    if (hasRequiredFields && stripeError) {
        return (
            <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-center">
                <p className="text-sm text-slate-700">{stripeError}</p>
                <button
                    type="button"
                    onClick={() => setStripeLoadAttempt((current) => current + 1)}
                    className="mt-3 inline-flex items-center gap-2 rounded-full bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900"
                >
                    <FaArrowsRotate className="h-3.5 w-3.5" />
                    Erneut versuchen
                </button>
            </div>
        );
    }

    const resolvedPaymentMethod = paymentMethod ?? null;

    if (hasRequiredFields && stripe && resolvedPaymentMethod) {
        return (
            <div className="rounded-xl bg-slate-50 p-4">
                <Elements options={options} stripe={stripe} key={`${resolvedPaymentMethod}-${clientSecret}`}>
                    <CheckoutStripe paymethod={resolvedPaymentMethod} email={email} price={total} returnPath={returnPath} />
                </Elements>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center justify-center rounded-xl bg-slate-50 p-8 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-200">
                <FaLock className="h-5 w-5 text-slate-400" />
            </div>
            <p className="text-sm text-slate-600">
                Bitte alle Felder ausfüllen und eine Zahlungsmethode auswählen.
            </p>
            {paymentError && (
                <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600">
                    Beim Bezahlvorgang ist ein Fehler aufgetreten. Bitte mit einer anderen Zahlungsmethode versuchen.
                </p>
            )}
            <button 
                disabled 
                className="mt-4 w-full cursor-not-allowed rounded-full bg-slate-300 py-4 font-semibold text-slate-500"
            >
                Jetzt bezahlen
            </button>
        </div>
    );
}
